import type {
    ComposableOptionsBase,
    MembershipInvitation,
    MembershipInviteCheck,
    MembershipInviteCheckFail,
    MembershipPending,
    Membership,
    MembershipInviteRequest,
    UserProfile
} from '~/types';
import type {
    CustomPagination,
    PaginatedRequest,
    PaginatedPayload
} from 'x1-base/types';
import type { NitroFetchOptions } from 'nitropack';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import { omit as lo_omit } from 'es-toolkit';
import type { NuxtError } from '#app';

export interface UseMembershipsOptions extends ComposableOptionsBase {}
export function useMemberships(options: Partial<UseMembershipsOptions> = {}) {
    const apiBasePath = `/v1/memberships`;
    const apiBasePathInvitations = `/v1/invitations`;
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });
    const {
        getNuxtErrorObj,
        getStatusTypeFromErrorObj,
        tryExtractObjFromError,
        isErrorOfStatusCode
    } = useApiUtils();


    async function getMembership(
        uid: string,
        expanders: string[] = []
    ): Promise<Membership | NuxtError> {
        const url = `${apiBasePath}/${uid}/`;

        // TODO: Might not need this anymore
        const expand = [
            'org.salesperson.employee',
            'team.salesperson.employee',
            ...expanders
        ].join(',');

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { expand }
        };

        try {
            return await $_fetch<Membership>(url, config);
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    async function getMemberships(): Promise<PaginatedPayload<Membership> | NuxtError> {
        // TODO: handle pagination params
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { expand: 'user', page_size: 100 }
        };

        const url = `${apiBasePath}/`;
        let payload: Membership[];
        let paginatedPayload: PaginatedPayload<Membership>;

        try {
            const response = await $_fetch<CustomPagination<Membership>>(url, config);
            payload = response.results;

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: payload
            };

            return paginatedPayload;
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    async function getMembershipPending(
        uid: string
    ): Promise<PaginatedPayload<MembershipPending> | NuxtError> {
        // TODO: handle pagination params
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { expand: ['user'], page_size: 100 }
        };

        const url = `${apiBasePath}/${uid}/pending-invitations/`;
        let payload: MembershipPending[];
        let paginatedPayload: PaginatedPayload<MembershipPending>;

        try {
            const response = await $_fetch<CustomPagination<MembershipPending>>(url, config);
            payload = response.results;

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: payload
            };

            return paginatedPayload;
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    async function deleteMembershipMember(
        uid: string
    ): Promise<boolean | NuxtError> {
        const url = `${apiBasePath}/${uid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'DELETE'
        };

        try {
            const response = await $_fetch.raw<unknown>(url, config);

            if (!response || response?.status > 399) {
                throw new Error('Error deleting item.');
            }

            return true;
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    async function getInvitations(
        args?: PaginatedRequest
    ): Promise<PaginatedPayload<MembershipInvitation> | NuxtError> {
        const url = args?.url ?? `${apiBasePathInvitations}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { page_size: 100 }
        };

        if (args?.params) {
            config.query = args?.params;
        }

        let paginatedPayload: PaginatedPayload<MembershipInvitation>;

        try {
            const response = await $_fetch<CustomPagination<MembershipInvitation>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }


    async function getInvitation(
        uid: string
    ): Promise<MembershipInvitation | MembershipInviteCheckFail | NuxtError> {
        const url = `${apiBasePathInvitations}/${uid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        try {
            return await $_fetch<MembershipInvitation>(url, config);
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    async function invitationCheck(
        uid: string
    ): Promise<MembershipInvitation | MembershipInviteCheckFail | NuxtError> {
        try {
            return await getInvitation(uid);
        } catch (err) {
            if (isErrorOfStatusCode(err as Error | NuxtError, 404)) {
                return {
                    failType: 'not-found'
                };
            }

            return getNuxtErrorObj(err);
        }
    }

    async function inviteAcceptDecline(
        uid: string,
        version: number,
        isAccept = true
    ): Promise<boolean | string | NuxtError> {
        const endpoint = isAccept ? 'accept' : 'decline';
        const url = `${apiBasePathInvitations}/${uid}/${endpoint}/`;
        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: {
                version
            }
        };

        try {
            await $_fetch<any>(url, config);
            return true;
        } catch (err) {
            const code = getStatusTypeFromErrorObj(err as Error | NuxtError);

            if (!!code && code < 5) {
                const errString = tryExtractObjFromError(err as Error | NuxtError);

                if (errString) {
                    return errString;
                }
            }

            return getNuxtErrorObj(err);
        }
    }

    async function sendMembershipInvites(
        rawEmails: string
    ): Promise<boolean | NuxtError> {
        const url = `${apiBasePathInvitations}/bulk-invite/`;

        const payload: MembershipInviteRequest = {
            rawEmails
        };

        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: payload
        };

        try {
            await $_fetch<UserProfile>(url, config);
            return true;
        } catch (err) {
            return getNuxtErrorObj(err);
        }
    }

    return {
        /**
        *  @deprecated Use getMembership
        */
        getItem: getMemberships,
        getMembership,
        getMemberships,
        getMembershipPending,
        deleteMembershipMember,
        getInvitations,
        getInvitation,
        invitationCheck,
        inviteAcceptDecline,
        sendMembershipInvites
    };
}
